import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { logsQueryOptions } from '../../api/admin';
import { Card } from '../common/Card';

interface AuditActivityCardProps {
  num: number;
  oid: string;
  /**
   * `actor` — what this person did. `target` — what was done to them.
   *
   * Two questions the two merged pages each answered, and only one of. The
   * table's third column follows: an actor's row names what they touched, a
   * target's row names who touched them.
   */
  mode: 'actor' | 'target';
  limit?: number;
}

export function AuditActivityCard({
  num,
  oid,
  mode,
  limit = 5,
}: AuditActivityCardProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery(
    logsQueryOptions(
      mode === 'actor'
        ? { actorOid: oid, limit, page: 1 }
        : { targetId: oid, limit, page: 1 },
    ),
  );

  const logs = data?.logs ?? [];
  const isActor = mode === 'actor';

  // A page about somebody with no history should not carry an empty table; the
  // reader's own page says "nothing yet", which is an answer they asked for.
  if (!isLoading && logs.length === 0 && !isActor) {
    return null;
  }

  return (
    <Card
      num={num}
      title={
        isActor ? t('profile.activity.title') : t('users.detail.audit.title')
      }
      meta={isActor ? t('profile.activity.meta') : t('users.detail.audit.meta')}
    >
      {isLoading ? (
        <p className="label cursor">LOADING</p>
      ) : logs.length === 0 ? (
        <p className="label">{t('profile.activity.empty')}</p>
      ) : (
        // The date column is `whitespace-nowrap` and the target is a GUID, so
        // this table has a floor of ~370px — wider than the card gets on a
        // phone, where it pushed the whole document 12px sideways. Scrolling
        // it inside its own box keeps the page still.
        <div className="overflow-x-auto">
          <table className="epi-table">
            <thead>
              <tr>
                <th>{t('profile.activity.colDate')}</th>
                <th>{t('profile.activity.colAction')}</th>
                <th>
                  {isActor
                    ? t('profile.activity.colTarget')
                    : t('users.detail.audit.colActor')}
                </th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="mono text-xs whitespace-nowrap">
                    {new Date(log.createdAt).toLocaleString()}
                  </td>
                  <td className="mono text-xs">{log.action}</td>
                  <td className="mono text-xs break-all">
                    {isActor
                      ? (log.targetId ?? '—')
                      : (log.actorName ?? log.actorOid)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
